import { Outlet, Link } from '@tanstack/react-router';
import { useAuth } from '../lib/auth';
import { getAppName } from '../lib/config';
import { getEnvironment } from '../lib/env';
import { LoginForm } from '../features/auth/LoginForm';
import { KeyGate } from '../features/keys/KeyGate';
import { useLocalConfigStatus } from '../features/keys/hooks';
import { EnvBanner } from './EnvBanner';
import { ErrorBoundary } from '../components/ErrorBoundary';

export function AuthedLayout() {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  // LOCAL box in remote-store mode needs a local-access key before login (login validates against the remote store).
  const { data: localConfig } = useLocalConfigStatus();

  if (isLoading) {
    return (
      <div data-id="em-mgr--shell--loading" className="flex min-h-full items-center justify-center">
        <span className="text-sm text-slate-500">Loading…</span>
      </div>
    );
  }

  if (localConfig?.needsKey) {
    // First-run gate: no remote key yet → paste one before login is possible.
    return (
      <>
        <EnvBanner />
        <KeyGate remoteUrl={localConfig.remoteUrl} />
      </>
    );
  }

  if (!isAuthenticated) {
    // Banner shown here too so the operator knows the environment before logging in, not just after.
    return (
      <>
        <EnvBanner />
        <LoginForm />
      </>
    );
  }

  return (
    <div data-id="em-mgr--shell--root" className="min-h-full bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <EnvBanner />
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3 dark:border-slate-800 dark:bg-slate-900">
        <nav className="flex items-center gap-4">
          <Link to="/" data-id="em-mgr--shell--home" className="font-semibold">
            {getAppName()}
          </Link>
          <Link
            to="/"
            data-id="em-mgr--shell--nav-fleet"
            activeOptions={{ exact: true }}
            className="text-sm text-slate-500 [&.active]:font-medium [&.active]:text-slate-900 dark:[&.active]:text-slate-100"
          >
            Fleet
          </Link>
          <Link
            to="/updates"
            data-id="em-mgr--shell--nav-updates"
            className="text-sm text-slate-500 [&.active]:font-medium [&.active]:text-slate-900 dark:[&.active]:text-slate-100"
          >
            Updates
          </Link>
          {/* Local-access keys are created + managed on the PRODUCTION manager only. */}
          {getEnvironment() === 'production' && (
            <Link
              to="/keys"
              data-id="em-mgr--shell--nav-keys"
              className="text-sm text-slate-500 [&.active]:font-medium [&.active]:text-slate-900 dark:[&.active]:text-slate-100"
            >
              Keys
            </Link>
          )}
          {/* Fleet credentials live in the store the PRODUCTION box owns — same posture as Keys. */}
          {getEnvironment() === 'production' && (
            <Link
              to="/integrations"
              data-id="em-mgr--shell--nav-integrations"
              className="text-sm text-slate-500 [&.active]:font-medium [&.active]:text-slate-900 dark:[&.active]:text-slate-100"
            >
              Integrations
            </Link>
          )}
        </nav>
        <div className="flex items-center gap-3 text-sm">
          <span data-id="em-mgr--shell--user" className="text-slate-500">
            {user?.email}
          </span>
          <button
            type="button"
            data-id="em-mgr--shell--logout"
            onClick={() => void logout()}
            className="rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700"
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl p-6">
        {/* A render throw in any routed screen must not blank the shell. */}
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
